import { useEffect, useRef } from "react";
import { Building2, User, Loader2 } from "lucide-react";
import { useAuthContext } from "@/providers/AuthProvider";
import logoImage from "@/assets/images/logo.png";

export function PortalHeader() {
  const {
    clientInfo,
    clientInfoLoading,
    getClientInfo,
    isAuthenticated,
  } = useAuthContext();
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (
      isAuthenticated &&
      !clientInfo &&
      !clientInfoLoading &&
      !hasFetchedRef.current
    ) {
      hasFetchedRef.current = true;
      getClientInfo().catch((error) => {
        console.error("Failed to fetch client info in PortalHeader:", error);
        hasFetchedRef.current = false; // Allow retry on next render
      });
    }
  }, [isAuthenticated, clientInfo, clientInfoLoading, getClientInfo]);

  return (
    <>
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-6">
            <a
              href={`https://${import.meta.env.VITE_PUBLIC_WEBAPP_URL}/`}
              className="flex items-center gap-3 group shrink-0"
            >
              <img
                src={logoImage}
                alt="Logo"
                className="h-10 w-auto object-contain"
              />
            </a>

            <div className="hidden md:block h-8 w-px bg-border/50" />

            <div className="hidden md:flex items-center gap-3">
              {clientInfoLoading ? (
                <div className="flex items-center gap-2 px-4 py-2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Loading...
                  </span>
                </div>
              ) : clientInfo ? (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm leading-tight">
                        {clientInfo.firstName} {clientInfo.lastName}
                      </p>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Building2 className="h-3 w-3" />
                        <span>{clientInfo.company}</span>
                        {clientInfo.companyIndustry && (
                          <>
                            <span>•</span>
                            <span>{clientInfo.companyIndustry}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground">
                  <User className="h-4 w-4" />
                  <span>Client Portal</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="md:hidden px-6 pb-3">
          {clientInfoLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Loading...</span>
            </div>
          ) : clientInfo ? (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm">
                  {clientInfo.firstName} {clientInfo.lastName}
                </p>
                <p className="text-xs text-muted-foreground">
                  {clientInfo.company}
                  {clientInfo.companyIndustry &&
                    ` • ${clientInfo.companyIndustry}`}
                </p>
              </div>
            </div>
          ) : null}
        </div>
      </header>
    </>
  );
}
